import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/common/Layout/Layout';
import ApiImage from '../../components/common/ApiImage';
import { BRAND } from '../../config/brand';
import { formatCurrencyDetailed } from '../../utils/currency';
import './AccountOrderDetail.css';

const BASE_URL   = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SITE_URL   = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://theluviajewels.com';
const BRAND_NAME = BRAND?.name || 'Luvia';

const getToken = () =>
  localStorage.getItem('token') || sessionStorage.getItem('token');

/* ─────────────────────────────────────────────
   Constants
───────────────────────────────────────────── */
const STATUS_META = {
  pending:          { label: 'Pending',           color: 'gray'   },
  confirmed:        { label: 'Confirmed',         color: 'gold'   },
  processing:       { label: 'Processing',        color: 'blue'   },
  shipped:          { label: 'Shipped',           color: 'teal'   },
  out_for_delivery: { label: 'Out for Delivery',  color: 'amber'  },
  delivered:        { label: 'Delivered',         color: 'green'  },
  cancelled:        { label: 'Cancelled',         color: 'red'    },
  returned:         { label: 'Returned',          color: 'purple' },
};

const TRACKING_STEPS = [
  { key: 'pending',          label: 'Placed',      icon: '✓' },
  { key: 'confirmed',        label: 'Confirmed',   icon: '✓' },
  { key: 'processing',       label: 'Processing',  icon: '✓' },
  { key: 'shipped',          label: 'Shipped',     icon: '✓' },
  { key: 'out_for_delivery', label: 'On the Way',  icon: '✓' },
  { key: 'delivered',        label: 'Delivered',   icon: '✓' },
];

const STEP_INDEX = Object.fromEntries(TRACKING_STEPS.map((s, i) => [s.key, i]));
// Map Shiprocket shippingStatus to timeline step (0-5)
const SHIPPING_TO_STEP = { pending: 0, order_created: 1, awb_assigned: 1, pickup_scheduled: 2, shipped: 3, in_transit: 3, out_for_delivery: 4, delivered: 5, cancelled: 0, rto: 0 };

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
/* Try every possible field name the API might return */
const resolveImage = (item) =>
  item?.image?.url ||  // Backend stores as { url: string, alt: string }
  item?.productImage ||
  item?.image ||  // Fallback for string format
  item?.images?.[0] ||
  item?.thumbnail ||
  item?.product?.images?.[0] ||
  null;


/* Real fallback: if order.total is 0 or missing, sum from items */
const resolveTotal = (order) => {
  if (order.pricing?.total && order.pricing.total > 0) return order.pricing.total;
  if (order.total && order.total > 0) return order.total;
  const itemsTotal = (order.items || []).reduce(
    (sum, item) => sum + (item.price || 0) * (item.quantity || 1),
    0
  );
  return itemsTotal;
};

const resolveSubtotal = (order) => {
  if (order.pricing?.subtotal && order.pricing.subtotal > 0) return order.pricing.subtotal;
  if (order.subtotal && order.subtotal > 0) return order.subtotal;
  return resolveTotal(order);
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
};

const formatDateISO = (iso) => {
  if (!iso) return '';
  return new Date(iso).toISOString().split('T')[0];
};

/* ─────────────────────────────────────────────
   Skeleton
───────────────────────────────────────────── */
function DetailSkeleton() {
  return (
    <div className="aod-skeleton" aria-hidden="true" aria-label="Loading order details">
      {/* Mobile summary skeleton */}
      <div className="aod-skeleton__summary-mobile">
        <div className="aod-sk aod-sk--60" />
        <div className="aod-sk aod-sk--40" />
      </div>
      {/* Tracking */}
      <div className="aod-skeleton__card">
        <div className="aod-sk aod-sk--40 aod-sk--h14" />
        <div className="aod-skeleton__track">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="aod-skeleton__track-step">
              <div className="aod-skeleton__dot" />
              <div className="aod-sk aod-sk--60 aod-sk--h10 aod-sk--mt6" />
            </div>
          ))}
        </div>
      </div>
      {/* Items */}
      <div className="aod-skeleton__card">
        {[1,2,3].map(i => (
          <div key={i} className="aod-skeleton__item">
            <div className="aod-skeleton__img" />
            <div className="aod-skeleton__iteminfo">
              <div className="aod-sk aod-sk--80" />
              <div className="aod-sk aod-sk--50" />
            </div>
            <div className="aod-sk aod-sk--30 aod-sk--w56" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   StatusBadge
───────────────────────────────────────────── */
function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, color: 'gray' };
  return (
    <span className={`aod-badge aod-badge--${meta.color}`}>
      {meta.label}
    </span>
  );
}

/* ─────────────────────────────────────────────
   Tracking Timeline
   Vertical on mobile (never overflows),
   horizontal on desktop.
───────────────────────────────────────────── */
function TrackingTimeline({ status, shippingStatus, estimatedDelivery, trackingNumber, trackingUrl, courierName }) {
  const currentStep = (shippingStatus != null && SHIPPING_TO_STEP[shippingStatus] != null)
    ? SHIPPING_TO_STEP[shippingStatus]
    : (STEP_INDEX[status] ?? -1);

  return (
    <div className="aod-track-wrap">
      {/* Vertical (mobile-first) */}
      <ol className="aod-track aod-track--vertical" aria-label="Order progress">
        {TRACKING_STEPS.map((step, i) => {
          const done   = i < currentStep;
          const active = i === currentStep;
          return (
            <li
              key={step.key}
              className={[
                'aod-track__step',
                done   ? 'aod-track__step--done'   : '',
                active ? 'aod-track__step--active' : '',
              ].filter(Boolean).join(' ')}
              aria-current={active ? 'step' : undefined}
            >
              <div className="aod-track__dot" aria-hidden="true">
                {done && (
                  <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <polyline points="2 6 5 9 10 3"/>
                  </svg>
                )}
              </div>
              <div className="aod-track__line" aria-hidden="true" />
              <div className="aod-track__info">
                <span className="aod-track__label">{step.label}</span>
                {active && estimatedDelivery && (
                  <span className="aod-track__eta">
                    Est.{' '}
                    <time dateTime={formatDateISO(estimatedDelivery)}>
                      {new Date(estimatedDelivery).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </time>
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Courier + Tracking number */}
      {(courierName || trackingNumber) && (
        <div className="aod-track__number">
          {courierName && <span className="aod-track__number-label">Courier: {courierName}</span>}
          {trackingNumber && (
            <>
              <span className="aod-track__number-label">AWB / Tracking ID</span>
              <code className="aod-track__number-value">{trackingNumber}</code>
            </>
          )}
          {trackingUrl && (
            <a
              href={trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="aod-track__number-link"
            >
              Track shipment
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   PaymentMethod — no emojis
───────────────────────────────────────────── */
function PaymentMethod({ method, cardLast4, razorpayOrderId }) {
  const methods = {
    cod:  { label: 'Cash on Delivery', icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
    )},
    upi:  { label: 'UPI Payment', icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
    )},
    card: { label: `Card ···· ${cardLast4 || '****'}`, icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
    )},
  };

  const m = methods[method] || { label: method, icon: null };

  return (
    <div className="aod-payment-row">
      {m.icon && <span className="aod-payment-icon" aria-hidden="true">{m.icon}</span>}
      <div>
        <p className="aod-payment-label">{m.label}</p>
        {razorpayOrderId && (
          <p className="aod-payment-ref">Ref: {razorpayOrderId}</p>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   OrderSummaryCard — reused in sticky mobile
   bar and desktop aside
───────────────────────────────────────────── */
function OrderSummaryCard({ order }) {
  const subtotal = resolveSubtotal(order);
  const total    = resolveTotal(order);
  const discount = order.pricing?.discount || 0;
  const shipping = order.pricing?.shipping || 0;

  return (
    <dl className="aod-summary">
      <div className="aod-summary__row">
        <dt>Subtotal</dt>
        <dd>{formatCurrencyDetailed(subtotal)}</dd>
      </div>
      {discount > 0 && (
        <div className="aod-summary__row aod-summary__row--discount">
          <dt>Discount</dt>
          <dd>−{formatCurrencyDetailed(discount)}</dd>
        </div>
      )}
      <div className="aod-summary__row">
        <dt>Shipping</dt>
        <dd>{shipping == null || shipping === 0 ? 'Free' : formatCurrencyDetailed(shipping)}</dd>
      </div>
      {/* Tax/GST not stored separately - included in pricing.total */}
      <div className="aod-summary__row aod-summary__row--total">
        <dt>Total</dt>
        <dd>{formatCurrencyDetailed(total)}</dd>
      </div>
    </dl>
  );
}

/* ─────────────────────────────────────────────
   Main Page
───────────────────────────────────────────── */
function AccountOrderDetail() {
  const { orderId } = useParams();
  const navigate    = useNavigate();

  const [order,   setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  /* ── Fetch ── */
  const fetchOrder = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/account/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        navigate('/login', {
          replace: true,
          state: { reason: 'SESSION_EXPIRED' },
        });
        return;
      }
      if (res.status === 404) { setError('Order not found.'); return; }
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      setOrder(data.order || data);
    } catch (err) {
      setError(err.message || 'Failed to load order.');
    } finally {
      setLoading(false);
    }
  }, [orderId, navigate]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  /* ── Reorder ── */
  const handleReorder = useCallback(() => {
    if (!order?.items) return;
    navigate('/cart', { state: { reorderItems: order.items } });
  }, [order, navigate]);

  /* ── Download Invoice (authenticated request) ── */
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const handleDownloadInvoice = useCallback(async () => {
    const token = getToken();
    if (!token || !orderId) return;
    setInvoiceLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/orders/${orderId}/invoice`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(res.status === 404 ? 'Invoice not found' : 'Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${order?.orderNumber || orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Invoice download failed', err);
    } finally {
      setInvoiceLoading(false);
    }
  }, [orderId, order?.orderNumber]);

  /* ── Derived ── */
  const orderNum    = order ? `#${order.orderNumber || order._id?.slice(-8).toUpperCase()}` : '';
  const dateISO     = order?.createdAt ? formatDateISO(order.createdAt) : '';
  const isCancelled = ['cancelled', 'returned'].includes(order?.status);
  const isDelivered = order?.status === 'delivered';
  const total       = order ? resolveTotal(order) : 0;

  /* ── Structured data ── */
  const jsonLd = order ? {
    '@context': 'https://schema.org',
    '@type': 'Order',
    orderNumber: order.orderNumber || order._id,
    orderDate: order.createdAt,
    orderStatus: `https://schema.org/Order${STATUS_META[order.status]?.label || 'Processing'}`,
    priceCurrency: 'INR',
    price: total,
    merchant: { '@type': 'Organization', name: BRAND_NAME },
    orderedItem: (order.items || []).map(item => ({
      '@type': 'OrderItem',
      name: item.name,
      orderQuantity: item.quantity || 1,
      orderItemStatus: 'https://schema.org/OrderDelivered',
      orderedItem: {
        '@type': 'Product',
        name: item.name,
        image: resolveImage(item),
      },
    })),
  } : null;

  return (
    <Layout showFooter={false} showBottomTab={true}>
      <Helmet>
        <title>Order {orderNum} | {BRAND_NAME}</title>
        <meta name="description" content={`Track your order ${orderNum} from ${BRAND_NAME}.`} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={orderId ? `${SITE_URL}/account/orders/${orderId}` : `${SITE_URL}/account/orders`} />
        {jsonLd && (
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        )}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'Account', item: `${SITE_URL}/account` },
            { '@type': 'ListItem', position: 3, name: 'Orders', item: `${SITE_URL}/account/orders` },
            { '@type': 'ListItem', position: 4, name: `Order ${orderNum}`, item: `${SITE_URL}/account/orders/${orderId}` },
          ],
        })}</script>
      </Helmet>

      <div className="aod-page">
        <div className="aod-container">

          {/* ── Breadcrumb ── */}
          <nav className="aod-breadcrumb" aria-label="Breadcrumb">
            <Link to="/account" className="aod-breadcrumb__link">Account</Link>
            <span className="aod-breadcrumb__sep" aria-hidden="true">›</span>
            <Link to="/account/orders" className="aod-breadcrumb__link">Orders</Link>
            {order && (
              <>
                <span className="aod-breadcrumb__sep" aria-hidden="true">›</span>
                <span className="aod-breadcrumb__current" aria-current="page">{orderNum}</span>
              </>
            )}
          </nav>

          {/* ── Page title ── */}
          <div className="aod-page-head">
            <div className="aod-page-head__text">
              <h1 className="aod-title">
                Order <span className="aod-title__num">{orderNum}</span>
              </h1>
              {order && (
                <time className="aod-placed" dateTime={dateISO}>
                  Placed {formatDate(order.createdAt)}
                </time>
              )}
            </div>
            {order && <StatusBadge status={order.status} />}
          </div>

          {/* ── Loading ── */}
          {loading && <DetailSkeleton />}

          {/* ── Error ── */}
          {!loading && error && (
            <div className="aod-error" role="alert">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <p className="aod-error__msg">{error}</p>
              <button className="aod-btn aod-btn--ghost" onClick={fetchOrder}>Try Again</button>
            </div>
          )}

          {/* ── Content ── */}
          {!loading && !error && order && (
            <>
              {/* Mobile sticky summary bar */}
              <div className="aod-sticky-bar" aria-label="Order total">
                <div className="aod-sticky-bar__total">
                  <span className="aod-sticky-bar__label">Total</span>
                  <span className="aod-sticky-bar__amount">{formatCurrencyDetailed(total)}</span>
                </div>
                <button
                  className="aod-btn aod-btn--primary"
                  onClick={handleReorder}
                  aria-label="Reorder all items from this order"
                >
                  Reorder
                </button>
                {isDelivered && (
                  <Link
                    to={`/account/orders/${orderId}/return`}
                    className="aod-btn aod-btn--ghost"
                  >
                    Return
                  </Link>
                )}
              </div>

              {/* Cancelled banner */}
              {isCancelled && (
                <div className="aod-banner aod-banner--cancelled" role="alert">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                  <span>
                    This order has been {order.status}.
                    {order.refundStatus && ` Refund ${order.refundStatus} — typically 5–7 business days.`}
                  </span>
                </div>
              )}

              {/* Two-col layout */}
              <div className="aod-layout">

                {/* ── LEFT: main content ── */}
                <div className="aod-main">

                  {/* Tracking */}
                  {!isCancelled && (
                    <section className="aod-card" aria-label="Order tracking">
                      <h2 className="aod-card__title">Order Tracking</h2>
                      <TrackingTimeline
                        status={order.status}
                        shippingStatus={order.shippingStatus}
                        estimatedDelivery={order.estimatedDeliveryDate || order.estimatedDelivery}
                        trackingNumber={order.awbCode || order.trackingNumber}
                        trackingUrl={order.trackingUrl}
                        courierName={order.courierName}
                      />
                      <div className="aod-track__invoice">
                        <button
                          type="button"
                          className="aod-track__number-link aod-track__invoice-btn"
                          onClick={handleDownloadInvoice}
                          disabled={invoiceLoading}
                          aria-label="Download invoice PDF"
                        >
                          {invoiceLoading ? 'Preparing…' : 'Download Invoice'}
                        </button>
                        {(order.invoiceNumber || order.invoice?.invoiceNumber) && (
                          <span className="aod-track__number-label">Invoice: {order.invoice?.invoiceNumber || order.invoiceNumber}</span>
                        )}
                      </div>
                      {order.orderTimeline && order.orderTimeline.length > 0 && (
                        <ul className="aod-timeline-list" aria-label="Order timeline">
                          {order.orderTimeline.slice().reverse().slice(0, 10).map((t, i) => (
                            <li key={i} className="aod-timeline-item">
                              <span className="aod-timeline-status">{t.status}</span>
                              {t.message && <span className="aod-timeline-msg">{t.message}</span>}
                              <time className="aod-timeline-time" dateTime={t.timestamp}>
                                {formatDate(t.timestamp)}
                              </time>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  )}

                  {/* Items */}
                  <section className="aod-card" aria-label={`Order items (${order.items?.length || 0})`}>
                    <h2 className="aod-card__title">
                      Items
                      <span className="aod-card__title-count">{order.items?.length || 0}</span>
                    </h2>
                    <div className="aod-items">
                      {(order.items || []).map((item, idx) => {
                        const img  = resolveImage(item);
                        const slug = item.slug || item.productSlug || item.product?.slug;
                        const lineTotal = (item.price || 0) * (item.quantity || 1);

                        return (
                          <div key={idx} className="aod-item">
                            {/* Image */}
                            <div className="aod-item__img-wrap">
                              {img ? (
                                <ApiImage
                                  src={img}
                                  fallback="/placeholder-jewelry.svg"
                                  alt={item.name}
                                  className="aod-item__img"
                                  loading="lazy"
                                />
                              ) : null}
                              <div
                                className={`aod-item__img-ph${img ? '' : ' is-visible'}`}
                                aria-hidden="true"
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                                  <circle cx="8.5" cy="8.5" r="1.5"/>
                                  <polyline points="21 15 16 10 5 21"/>
                                </svg>
                              </div>
                            </div>

                            {/* Info */}
                            <div className="aod-item__info">
                              {slug ? (
                                <Link to={`/product/${slug}`} className="aod-item__name-link">
                                  <p className="aod-item__name">{item.name}</p>
                                </Link>
                              ) : (
                                <p className="aod-item__name">{item.name}</p>
                              )}
                              <p className="aod-item__attrs">
                                {[
                                  item.size  && `Size ${item.size}`,
                                  item.color && item.color,
                                  `Qty ${item.quantity || 1}`,
                                ].filter(Boolean).join(' · ')}
                              </p>
                              {item.price && (
                                <p className="aod-item__unit-price">
                                  {formatCurrencyDetailed(item.price)} each
                                </p>
                              )}
                            </div>

                            {/* Line total */}
                            <p className="aod-item__total">
                              {formatCurrencyDetailed(lineTotal)}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  {/* Delivery address */}
                  {order.address && (
                    <section className="aod-card" aria-label="Delivery address">
                      <h2 className="aod-card__title">Delivery To</h2>
                      <address className="aod-address">
                        <strong>{order.address.fullName}</strong>
                        <span>{order.address.line1}</span>
                        {order.address.line2 && <span>{order.address.line2}</span>}
                        <span>
                          {[
                            order.address.city,
                            order.address.state,
                            order.address.pincode,
                          ].filter(Boolean).join(', ')}
                        </span>
                        {order.address.phone && (
                          <span className="aod-address__phone">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.83a19.79 19.79 0 01-3.07-8.63A2 2 0 012 2h3a2 2 0 012 1.72c.13 1 .36 1.97.71 2.9a2 2 0 01-.45 2.11L6.09 9.91a16 16 0 006 6l1.18-1.17a2 2 0 012.11-.45c.93.35 1.9.58 2.9.71A2 2 0 0122 16.92z"/>
                            </svg>
                            {order.address.phone}
                          </span>
                        )}
                      </address>
                    </section>
                  )}

                  {/* Payment */}
                  {order.payment?.method && (
                    <section className="aod-card" aria-label="Payment method">
                      <h2 className="aod-card__title">Payment</h2>
                      <PaymentMethod
                        method={order.payment.method}
                        cardLast4={order.payment?.cardLast4}
                        razorpayOrderId={order.payment?.razorpayOrderId}
                      />
                    </section>
                  )}
                </div>

                {/* ── RIGHT: aside ── */}
                <aside className="aod-aside" aria-label="Order summary">

                  {/* Summary card */}
                  <div className="aod-card aod-card--sticky">
                    <h2 className="aod-card__title">Order Summary</h2>
                    <OrderSummaryCard order={order} />

                    {/* Actions */}
                    <div className="aod-aside-actions">
                      <button
                        className="aod-btn aod-btn--primary aod-btn--full"
                        onClick={handleReorder}
                        aria-label="Reorder all items from this order"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <polyline points="23 4 23 10 17 10"/>
                          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                        </svg>
                        Reorder
                      </button>
                      {isDelivered && (
                        <Link
                          to={`/account/orders/${orderId}/return`}
                          className="aod-btn aod-btn--ghost aod-btn--full"
                        >
                          Return / Exchange
                        </Link>
                      )}
                    </div>
                  </div>

                </aside>
              </div>
            </>
          )}

        </div>
      </div>
    </Layout>
  );
}

export default AccountOrderDetail;
